// AdAccountList.tsx - Material React Table 버전
import { useState } from 'react';
import { MaterialReactTable } from 'material-react-table';
import { LegionExposeData } from './useLoadLegionExpose';
import { Box, IconButton, styled } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import icon_ArrowUp from 'src/assets/images/Icon_ArrowUp.svg';
import icon_ArrowDown from 'src/assets/images/Icon_ArrowDown.svg';

const TableHeaderSx = {
  backgroundColor: '#30515B',
  color: 'white',
  fontSize: '14px',
  textAlign: 'center',
  '& .Mui-TableHeadCell-Content': {
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
    height: '100%',
  },
  '&:first-of-type': {
    borderTopLeftRadius: '5px',
    borderBottomLeftRadius: '5px',
  },
  '&:last-of-type': {
    borderTopRightRadius: '5px',
    borderBottomRightRadius: '5px',
  },
}

const TableRowSx = {
  height: '49px',
  '& > td:first-of-type': {
    borderTopLeftRadius: '5px',
    borderBottomLeftRadius: '5px',
  },
  '& > td:last-of-type': {
    borderTopRightRadius: '5px',
    borderBottomRightRadius: '5px',
  },
  '& > td': {
    background: '#FFF',
    boxShadow: '1px 2px 2px rgba(0, 0, 0, 0.1)',
    padding: '4px',
  },
}

const TableCellSx = {
  textAlign: 'center',
  verticalAlign: 'middle',
  fontSize: '16px',
}

const RateGap = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isPositive', // styled에 전달하지 않도록 필터링
})<{isPositive : number}>(({ theme, isPositive }) => ({
    display:'flex',
    justifyContent:'center',
    alignItems:'center',

    padding:'5px 10px',
    fontSize:'12px',
    fontWeight: 600,
    backgroundColor:
        isPositive > 0
            ? '#FFEEEE' // 양수일 때 (연한 초록)
            : isPositive < 0
            ? '#E8F1FF' // 음수일 때 (연한 빨강)
            : '#E0E0E0', // 0일 때 (회색)
    color:
        isPositive > 0
            ? '#FF5A5A' // 양수일 때 (연한 초록)
            : isPositive < 0
            ? '#1976FF' // 음수일 때 (연한 빨강)
            : '#727272', // 0일 때 (회색)
    maxHeight:'24px',
    maxWidth:'77px',
    borderRadius:'3px'
}))

export const getStatusFromRow = (row: any): string => {
  const { is_active, admin_approval } = row;
  if (!is_active && !admin_approval) return '대기';
  if (!is_active && admin_approval) return '차단';
  if (is_active && admin_approval) return '승인';
  return ''; // 기타 상태는 필터링 제외
};

// const icons : Partial<MRT_Icons> = {
//   // ArrowDownwardIcon: () => 
//   // <ArrowDropUpIcon sx={{color:'red'}} className="MuiTableSortLabel-icon"/>,
//   SortIcon: (props: any) => (
//     <ArrowDropUpIcon
//       {...props}
//       className={`sort-icon ${props.className ?? ''}`}
//       sx={{
//         transition: 'transform 0.2s ease',
//       }}
//     />
//   ),
// }

interface Props{
  propsData : LegionExposeData[]
}

const Legion_ExposeTable = ({propsData}:Props) => {
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 8,
  });
  const totalPages = Math.ceil(propsData.length / pagination.pageSize);

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, pageIndex: page }));
  };
  
  return (
    <div style={{height:'100%'}}>
    <MaterialReactTable
      data={propsData}
      muiTableContainerProps={{
        sx: { 
          height: 'calc(100vh - 450px)',
          overflowY: 'auto',
          overflowX: 'auto',
          width: 'calc(100vw - 425px)'
        }
      }}
      columns={[
        {
          header: '순서',
          Cell: ({ row }) => row.index + 1, // 0부터 시작하므로 +1
          size: 50, // 필요에 따라 너비 지정
          enableSorting: false, // ✅ 정렬 비활성화
        },
        { accessorKey: 'wide_loc', header: '지역' },
        { accessorKey: 'total_school_count', header: '전체 학교 수' },
        { accessorKey: 'current_promoted_school_count', header: '홍보 완료 학교 수' },
        {
          header: '학교 노출도(전년대비 증감)',
          accessorFn: (row) => {
            const total = row.total_school_count;
            const current = row.current_promoted_school_count;
            const prev = row.prev_year_promoted_school_count;

            const currentRate = total && total !== 0 ? (current / total) * 100 : 0;
            const prevRate = total && total !== 0 ? (prev / total) * 100 : 0;
            const diff = currentRate - prevRate;

            return {
              currentRate,
              diff,
            };
          },
          sortingFn: (rowA, rowB, columnId) => {
            const a = rowA.getValue(columnId) as { currentRate: number };
            const b = rowB.getValue(columnId) as { currentRate: number };
            return a.currentRate - b.currentRate;
          },
          Cell: ({ cell }) => {
            const { currentRate, diff } = cell.getValue() as { currentRate: number; diff: number };
            const absDiff = Math.abs(diff).toFixed(1);

            return (
              <div style={{display : 'flex', justifyContent:'center', alignItems:'center'}}>
                {currentRate.toFixed(1)}%
                <RateGap isPositive={diff}>
                  {
                  diff > 0 ? <img src={icon_ArrowUp}/> :
                  diff < 0 ? <img src={icon_ArrowDown}/> :
                  <span style={{paddingRight:'5px', fontWeight:'bold', fontSize:'18px'}}>-</span> // 같을 때
                  }
                  {absDiff}%
                </RateGap>
              </div>
            )
          },
        },
        { accessorKey: 'total_student_count', header: '전체 학생 수' },
        { accessorKey: 'current_promoted_student_count', header: '참여 학생 수' },
        {
          header: '학생 노출도(전년대비)',
          accessorFn: (row) => {
            const total = row.total_student_count;
            const current = row.current_promoted_student_count;
            const prev = row.prev_year_promoted_student_count;

            const currentRate = total && total !== 0 ? (current / total) * 100 : 0;
            const prevRate = total && total !== 0 ? (prev / total) * 100 : 0;
            const diff = currentRate - prevRate;

            return {
              currentRate,
              diff,
            };
          },
          sortingFn: (rowA, rowB, columnId) => {
            const a = rowA.getValue(columnId) as { currentRate: number };
            const b = rowB.getValue(columnId) as { currentRate: number };
            return a.currentRate - b.currentRate;
          },
          Cell: ({ cell }) => {
            const { currentRate, diff } = cell.getValue() as { currentRate: number; diff: number };
            const absDiff = Math.abs(diff).toFixed(1);

            return (
              <div style={{display : 'flex', justifyContent:'center', alignItems:'center'}}>
                {currentRate.toFixed(1)}%
                <RateGap isPositive={diff}>
                  {
                  diff > 0 ? <img src={icon_ArrowUp}/> :
                  diff < 0 ? <img src={icon_ArrowDown}/> :
                  <span style={{paddingRight:'5px', fontWeight:'bold', fontSize:'18px'}}>-</span> // 같을 때
                  }
                  {absDiff}%
                </RateGap>
              </div>
            )
          },
        }
      ]}
      // 툴바, sort 버튼 등
      enableTopToolbar={false}
      enableBottomToolbar={false}
      enableColumnActions={false}
      enableSorting={true}
      muiTablePaperProps={{
        elevation: 0, // ✅ 그림자 제거
      }}
      // 로우 헤더 스타일
      muiTableHeadCellProps={{
        sx: TableHeaderSx,
      }}
      // 로우 스타일
      muiTableBodyRowProps={{
      sx: TableRowSx,
      }}
      // 셀 스타일
      muiTableBodyCellProps={{
        sx: TableCellSx,
      }}
      // 테이블 스타일
      muiTableProps={{
        sx: {
          borderCollapse: 'separate',
          borderSpacing: '0 5px', // ✅ row 간격 5px
          '& .MuiTableSortLabel-icon': {
            color: '#aaa',
            fontSize: '1.5rem',
          },
          '& .MuiTableSortLabel-root.Mui-active .MuiTableSortLabel-icon': {
            color: 'rgb(255, 255, 255) !important',
          },
      }}}
      // 페이지 네이션 옵션
      state={{ pagination }}
      onPaginationChange={setPagination}
      pageCount={totalPages} // 수동 계산 필요 시
      // icons={icons}
      />
        <Box display="flex" justifyContent="center" mt={2} gap={1}>
          <IconButton
            onClick={() => handlePageChange(Math.max(pagination.pageIndex - 1, 0))}
            disabled={pagination.pageIndex === 0}
            aria-label="이전 페이지"
          >
            <ChevronLeft />
          </IconButton>

          {Array.from({ length: totalPages }).map((_, index) => (
            <IconButton
              key={index}
              onClick={() => handlePageChange(index)}
              sx={{
                backgroundColor:
                  pagination.pageIndex === index ? '#50818b' : 'transparent',
                color: pagination.pageIndex === index ? '#fff' : '#666',
                '&:hover': {
                  backgroundColor:
                    pagination.pageIndex === index ? '#4a747d' : '#f0f0f0',
                },
                width: 32,
                height: 32,
                fontSize: 14,
              }}
            >
              {index + 1}
            </IconButton>
          ))}

          <IconButton
            onClick={() =>
              handlePageChange(Math.min(pagination.pageIndex + 1, totalPages - 1))
            }
            disabled={pagination.pageIndex === totalPages - 1}
            aria-label="다음 페이지"
          >
            <ChevronRight />
          </IconButton>
      </Box>
    </div>
  );
};

export default Legion_ExposeTable;
